/**
 * Người mua cá nhân của hoá đơn eShop — ô tích «Khách không cung cấp thông tin
 * xuất hoá đơn» + tên thật / địa chỉ / giấy tờ (CEO 24/10/2026, spec mục 7).
 *
 * Tích ô (mặc định) ⇒ hiện dòng «Người mua: Bán cho người tiêu dùng» để lễ tân
 * chép sang MISA eShop. Bỏ tích ⇒ tên thật bắt buộc; địa chỉ + giấy tờ tuỳ khách.
 * Trang đặt lịch công khai không hỏi giấy tờ (`askId = false`).
 */
import { Checkbox } from "@/ui/checkbox";
import { Label } from "@/ui/label";
import { cn } from "@/lib/utils";
import { InvoiceField } from "./InvoiceField";
import {
  CONSUMER_BUYER_NAME,
  validatePersonBuyer,
  type PersonBuyer,
  type PersonIdKind,
} from "./personalInvoiceBuyer";

type Props = {
  idPrefix: string;
  person: PersonBuyer;
  onChange: (next: PersonBuyer) => void;
  showErrors: boolean;
  askId: boolean;
  /** Khoá cả khối (hoá đơn đã xuất từ hôm trước, CEO 6b) — lý do do màn cha hiện. */
  disabled?: boolean;
  /** Trang khách tự đặt: lời nói với khách thay vì với nhân sự. */
  forCustomer?: boolean;
};

const ID_KINDS: { value: PersonIdKind; label: string }[] = [
  { value: "", label: "Không có" },
  { value: "cccd", label: "CCCD" },
  { value: "passport", label: "Hộ chiếu" },
];

export function PersonalBuyerFields({
  idPrefix,
  person,
  onChange,
  showErrors,
  askId,
  disabled,
  forCustomer,
}: Props) {
  const errors = validatePersonBuyer("personal", person);
  const err = (k: keyof typeof errors) => (showErrors ? errors[k] : undefined);
  const set = (patch: Partial<PersonBuyer>) => onChange({ ...person, ...patch });
  const checkboxId = `${idPrefix}-invoice-consumer`;

  return (
    <div className="space-y-3">
      <label htmlFor={checkboxId} className="flex min-h-11 cursor-pointer items-start gap-2">
        <Checkbox
          id={checkboxId}
          className="mt-0.5"
          checked={!person.provided}
          disabled={disabled}
          onCheckedChange={(v) => set({ provided: v !== true })}
        />
        <span className="text-sm">
          {forCustomer
            ? "Không cần ghi thông tin người mua trên hoá đơn"
            : "Khách không cung cấp thông tin xuất hoá đơn"}
        </span>
      </label>

      {!person.provided ? (
        <p className="rounded-xl border border-dashed bg-surface px-3 py-2 text-sm">
          Người mua: <span className="font-medium">{CONSUMER_BUYER_NAME}</span>
        </p>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          <InvoiceField
            id={`${idPrefix}-invoice-person-name`}
            label={forCustomer ? "Họ tên ghi trên hoá đơn" : "Tên thật của người mua"}
            value={person.name}
            onChange={(name) => set({ name })}
            error={err("name")}
            placeholder="Nguyễn Thị Lan"
            autoComplete="name"
            maxLength={200}
            disabled={disabled}
            className="md:col-span-2"
          />
          <InvoiceField
            id={`${idPrefix}-invoice-person-address`}
            label="Địa chỉ (không bắt buộc)"
            value={person.address}
            onChange={(address) => set({ address })}
            error={err("address")}
            placeholder="Số nhà, đường, phường, tỉnh/thành"
            autoComplete="street-address"
            maxLength={300}
            disabled={disabled}
            className="md:col-span-2"
          />
          {askId && (
            <IdDocFields
              idPrefix={idPrefix}
              person={person}
              set={set}
              err={err}
              disabled={disabled}
            />
          )}
        </div>
      )}
    </div>
  );
}

/** Loại giấy tờ + số + quốc tịch (chỉ màn nội bộ hỏi). */
function IdDocFields({
  idPrefix,
  person,
  set,
  err,
  disabled,
}: {
  idPrefix: string;
  person: PersonBuyer;
  set: (patch: Partial<PersonBuyer>) => void;
  err: (k: "idNumber" | "idNationality") => string | undefined;
  disabled: boolean | undefined;
}) {
  return (
    <>
      <div className="space-y-1.5 md:col-span-2">
        <Label>Giấy tờ (không bắt buộc)</Label>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Loại giấy tờ">
          {ID_KINDS.map((k) => (
            <button
              key={k.value || "none"}
              type="button"
              disabled={disabled}
              aria-pressed={person.idKind === k.value}
              onClick={() =>
                set({
                  idKind: k.value,
                  ...(k.value === "" ? { idNumber: "", idNationality: "" } : {}),
                  ...(k.value !== "passport" ? { idNationality: "" } : {}),
                })
              }
              className={cn(
                "min-h-11 rounded-xl border bg-surface px-3 text-sm transition disabled:opacity-50",
                person.idKind === k.value
                  ? "border-primary bg-primary/5 font-medium"
                  : "hover:border-primary/40",
              )}
            >
              {k.label}
            </button>
          ))}
        </div>
      </div>
      {person.idKind !== "" && (
        <InvoiceField
          id={`${idPrefix}-invoice-person-id`}
          label={person.idKind === "cccd" ? "Số CCCD" : "Số hộ chiếu"}
          value={person.idNumber}
          onChange={(idNumber) => set({ idNumber })}
          error={err("idNumber")}
          placeholder={person.idKind === "cccd" ? "001234567890" : "M1234567"}
          {...(person.idKind === "cccd" ? { inputMode: "numeric" as const } : {})}
          maxLength={24}
          disabled={disabled}
        />
      )}
      {person.idKind === "passport" && (
        <InvoiceField
          id={`${idPrefix}-invoice-person-nationality`}
          label="Quốc tịch"
          value={person.idNationality}
          onChange={(idNationality) => set({ idNationality })}
          error={err("idNationality")}
          placeholder="Hàn Quốc"
          maxLength={60}
          disabled={disabled}
        />
      )}
    </>
  );
}
